import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  CircleDashed,
  Eye,
  KeyRound,
  Loader2,
  Plug,
  RefreshCw,
  RotateCcw,
  Server,
  ShieldAlert,
  Unplug,
} from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

type Base44ConnectionStatus = {
  id: string;
  status: string;
  auth_mode: string;
  health: string;
  account_email: string | null;
  account_id: string | null;
  workspace_name: string | null;
  workspace_id: string | null;
  scopes: string[];
  token_expires_at: string | null;
  connected_at: string | null;
  disconnected_at: string | null;
  last_checked_at: string | null;
  last_refresh_at: string | null;
  last_success_at: string | null;
  last_error: string | null;
  last_error_at: string | null;
  failure_count: number;
  provider_identity: ProviderIdentitySnapshot | null;
  provider_identity_error: string | null;
  provider_identity_checked_at: string | null;
  app_access_probe: Base44AppAccessProbe | null;
  updated_at: string;
};

type Base44AppAccessProbe = {
  checkedAt?: string | null;
  status?: "ok" | "skipped" | "tool_missing" | "failed" | string;
  reason?: string | null;
  targetSource?: "env" | "latest_ready_os" | "latest_os" | "none" | string;
  appIdHash?: string | null;
  toolCount?: number | null;
  tools?: {
    getAppStatus?: boolean;
    getAppPreviewUrl?: boolean;
  } | null;
  appStatus?: {
    state?: string | null;
    providerStatus?: string | null;
    keys?: string[];
  } | null;
  preview?: {
    toolAvailable?: boolean;
    toolSucceeded?: boolean;
    originHash?: string | null;
    urlHash?: string | null;
    hasPreviewToken?: boolean | null;
    httpStatus?: number | null;
    hasTitle?: boolean | null;
    classification?: "ready" | "preview_loading" | "login" | "error" | "not_checked" | string;
  } | null;
};

type ProviderIdentitySnapshot = {
  observed_at?: string | null;
  source?: string | null;
  user_email?: string | null;
  organization_id?: string | null;
  workspace_id?: string | null;
  workspace_name?: string | null;
  tier?: string | null;
  tier_display_name?: string | null;
  is_over_limit?: boolean | null;
  daily_usage?: number | null;
  daily_limit?: number | null;
  monthly_usage?: number | null;
  monthly_limit?: number | null;
  monthly_integrations_usage?: number | null;
  monthly_integrations_limit?: number | null;
  monthly_integrations_is_over_limit?: boolean | null;
  next_period_start_date?: string | null;
  current_credit_period_start_date?: string | null;
  current_credit_period_end_date?: string | null;
  past_due?: boolean | null;
};

type Base44StatusPayload = {
  ok?: boolean;
  error?: string;
  code?: string;
  needsReconnect?: boolean;
  connection: Base44ConnectionStatus;
  blocked: {
    jobs: number;
    auth_blocked_apps: number;
    update_auth_blocked_apps: number;
  };
  runtime: {
    oauthConfigured: boolean;
    missingOAuth: string[];
    scopes: string;
    deviceOAuthConfigured: boolean;
    deviceOAuthScopes: string;
    encryptionConfigured: boolean;
    securityAlertRecipientsConfigured: boolean;
    legacyTokenFallbackEnabled: boolean;
  };
};

type Base44DeviceAuthorization = {
  flow_id: string;
  user_code: string;
  verification_uri: string;
  expires_at: string;
  interval_seconds: number;
};

type Base44DevicePollPayload = Partial<Base44StatusPayload> & {
  ok?: boolean;
  error?: string;
  authorization_status?: "pending" | "connected";
  retry_after_seconds?: number;
};

const HEALTH_LABELS: Record<string, string> = {
  connected: "Connected",
  manual_token: "Manual token",
  legacy_env: "Legacy dev token",
  expiring: "Expiring soon",
  expired: "Expired",
  failed: "Refresh failed",
  degraded: "Reconnect required",
  reconnect_required: "Reconnect required",
  disconnected: "Disconnected",
};

function providerText(value?: string | null): string {
  return (value ?? "").replace(/base44/gi, "Provider");
}

function providerActionMessage(data: Base44StatusPayload): string {
  if (data.needsReconnect) return "Provider token is invalid. Reconnect Provider to continue.";
  return providerText(data.error ?? "Provider action did not complete.");
}

function formatDate(value?: string | null): string {
  if (!value) return "Not yet";
  return new Date(value).toLocaleString();
}

function formatExpiry(value?: string | null): string {
  if (!value) return "No expiry returned";
  const expiresAt = new Date(value).getTime();
  if (!Number.isFinite(expiresAt)) return formatDate(value);
  const diffMinutes = Math.round((expiresAt - Date.now()) / 60000);
  const suffix = diffMinutes >= 0
    ? `${Math.max(1, Math.round(diffMinutes / 60))}h remaining`
    : `${Math.abs(Math.round(diffMinutes / 60))}h ago`;
  return `${formatDate(value)} (${suffix})`;
}

function formatNumber(value?: number | null): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "Unknown";
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function formatUsage(used?: number | null, limit?: number | null): string {
  const usedLabel = formatNumber(used);
  if (usedLabel === "Unknown" && (typeof limit !== "number" || !Number.isFinite(limit))) return "Unknown";
  const limitLabel = typeof limit === "number" && Number.isFinite(limit)
    ? limit.toLocaleString(undefined, { maximumFractionDigits: 2 })
    : "no limit";
  return `${usedLabel} / ${limitLabel}`;
}

function formatIdentityValue(value?: string | null): string {
  return value?.trim() || "Unknown";
}

function healthClass(health: string): string {
  if (health === "connected") return "border-emerald-500/40 bg-emerald-500/10 text-emerald-700";
  if (health === "manual_token" || health === "legacy_env" || health === "expiring" || health === "degraded") return "border-amber-500/50 bg-amber-500/10 text-amber-800";
  if (health === "reconnect_required") return "border-destructive/40 bg-destructive/10 text-destructive";
  if (health === "expired" || health === "failed") return "border-destructive/40 bg-destructive/10 text-destructive";
  return "border-border bg-muted text-muted-foreground";
}

function statusIcon(health: string) {
  if (health === "connected") return CheckCircle2;
  if (health === "expired" || health === "failed" || health === "reconnect_required") return ShieldAlert;
  if (health === "manual_token" || health === "legacy_env" || health === "expiring" || health === "degraded") return AlertTriangle;
  return Unplug;
}

function appAccessStatusLabel(probe?: Base44AppAccessProbe | null, checking = false): string {
  if (checking) return "Checking app access";
  if (!probe) return "Not checked";
  if (probe.status === "ok") return "Can access apps";
  if (probe.status === "skipped") return "No app target";
  if (probe.status === "tool_missing") return "Check unavailable";
  if (probe.status === "failed") return "Access check failed";
  return probe.status ? providerText(probe.status.replace(/_/g, " ")) : "Unknown";
}

function appAccessStatusClass(probe?: Base44AppAccessProbe | null, checking = false): string {
  if (checking) return "border-blue-500/40 bg-blue-500/10 text-blue-800";
  if (!probe) return "border-border bg-muted text-muted-foreground";
  if (probe.status === "ok") return "border-emerald-500/40 bg-emerald-500/10 text-emerald-700";
  if (probe.status === "failed") return "border-destructive/40 bg-destructive/10 text-destructive";
  if (probe.status === "tool_missing" || probe.status === "skipped") return "border-amber-500/50 bg-amber-500/10 text-amber-800";
  return "border-border bg-muted text-muted-foreground";
}

function appAccessIcon(probe?: Base44AppAccessProbe | null, checking = false) {
  if (checking) return Loader2;
  if (probe?.status === "ok") return CheckCircle2;
  if (probe?.status === "failed") return ShieldAlert;
  if (probe?.status === "tool_missing" || probe?.status === "skipped") return AlertTriangle;
  return CircleDashed;
}

function previewLabel(classification?: string | null): string {
  if (!classification || classification === "not_checked") return "Not checked";
  if (classification === "ready") return "Rendered";
  if (classification === "preview_loading") return "Still loading";
  if (classification === "login") return "Login page";
  if (classification === "error") return "Error";
  return providerText(classification.replace(/_/g, " "));
}

function targetSourceLabel(source?: string | null): string {
  if (source === "env") return "Configured health app";
  if (source === "latest_ready_os") return "Latest ready App";
  if (source === "latest_os") return "Latest stored App";
  if (source === "none") return "No app target";
  return source ? providerText(source.replace(/_/g, " ")) : "Unknown";
}

function yesNo(value?: boolean | null): string {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return "Unknown";
}

export function Base44Connection() {
  const { toast } = useToast();
  const [status, setStatus] = useState<Base44StatusPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualToken, setManualToken] = useState("");
  const [manualExpiresAt, setManualExpiresAt] = useState("");
  const [manualConfirmed, setManualConfirmed] = useState(false);
  const [deviceAuth, setDeviceAuth] = useState<Base44DeviceAuthorization | null>(null);
  const [deviceOpen, setDeviceOpen] = useState(false);
  const [devicePolling, setDevicePolling] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke<Base44StatusPayload>("base44-connection", {
        body: { action: "status" },
      });
      if (error) throw error;
      setStatus(data ?? null);
    } catch (error) {
      toast({
        title: "Unable to load Provider connection",
        description: error instanceof Error ? providerText(error.message) : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void loadStatus();

    const params = new URLSearchParams(window.location.search);
    if (params.get("base44") === "connected") {
      toast({ title: "Provider connected" });
    }
    const callbackError = params.get("base44_error");
    if (callbackError) {
      toast({
        title: "Provider connection failed",
        description: callbackError.replace(/_/g, " "),
        variant: "destructive",
      });
    }
  }, [loadStatus, toast]);

  const blockedTotal = useMemo(() => {
    if (!status) return 0;
    return status.blocked.jobs + status.blocked.auth_blocked_apps + status.blocked.update_auth_blocked_apps;
  }, [status]);

  const runAction = async (action: string, successTitle: string, body: Record<string, unknown> = {}) => {
    setBusyAction(action);
    try {
      const { data, error } = await supabase.functions.invoke<Base44StatusPayload>("base44-connection", {
        body: { action, ...body },
      });
      if (error) throw error;
      if (data?.ok === false) {
        if (data.connection) setStatus(data);
        toast({
          title: data.needsReconnect ? "Provider reconnect required" : "Provider action did not complete",
          description: providerActionMessage(data),
        });
        return;
      }
      if (data) setStatus(data);
      toast({ title: successTitle });
    } catch (error) {
      toast({
        title: "Provider action failed",
        description: error instanceof Error ? providerText(error.message) : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setBusyAction(null);
    }
  };

  const startDeviceOAuth = async () => {
    setBusyAction("device_oauth_start");
    try {
      const { data, error } = await supabase.functions.invoke<Base44DeviceAuthorization & { error?: string }>("base44-connection", {
        body: {
          action: "device_oauth_start",
          redirectOrigin: window.location.origin,
          redirectPath: "/admin?tab=base44",
        },
      });
      if (error) throw error;
      if (!data?.flow_id || !data.verification_uri || !data.user_code) {
        throw new Error(data?.error ?? "Provider authorization details were not returned");
      }
      setDeviceAuth(data);
      setDeviceOpen(true);
    } catch (error) {
      toast({
        title: "Unable to start Provider authorization",
        description: error instanceof Error ? providerText(error.message) : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setBusyAction(null);
    }
  };

  const pollDeviceOAuth = useCallback(async () => {
    if (!deviceAuth || devicePolling) return;
    if (new Date(deviceAuth.expires_at).getTime() <= Date.now()) {
      setDeviceAuth(null);
      setDeviceOpen(false);
      toast({
        title: "Provider authorization expired",
        description: "Start the connection again to receive a new code.",
        variant: "destructive",
      });
      return;
    }

    setDevicePolling(true);
    try {
      const { data, error } = await supabase.functions.invoke<Base44DevicePollPayload>("base44-connection", {
        body: {
          action: "device_oauth_poll",
          flowId: deviceAuth.flow_id,
        },
      });
      if (error) throw error;
      if (data?.authorization_status === "pending") {
        setDeviceAuth((current) => current
          ? { ...current, interval_seconds: data.retry_after_seconds ?? current.interval_seconds }
          : current);
        return;
      }
      if (data?.authorization_status !== "connected" || !data.connection || !data.blocked || !data.runtime) {
        throw new Error(data?.error ?? "Provider authorization did not complete.");
      }
      setStatus(data as Base44StatusPayload);
      setDeviceAuth(null);
      setDeviceOpen(false);
      toast({ title: "Provider connected" });
    } catch (error) {
      setDeviceAuth(null);
      setDeviceOpen(false);
      toast({
        title: "Provider authorization failed",
        description: error instanceof Error ? providerText(error.message) : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setDevicePolling(false);
    }
  }, [deviceAuth, devicePolling, toast]);

  useEffect(() => {
    if (!deviceOpen || !deviceAuth) return;
    const timeoutId = window.setTimeout(
      () => void pollDeviceOAuth(),
      Math.max(1, deviceAuth.interval_seconds) * 1000,
    );
    return () => window.clearTimeout(timeoutId);
  }, [deviceAuth, deviceOpen, pollDeviceOAuth]);

  const saveManualToken = async () => {
    if (!manualToken.trim() || !manualConfirmed) return;
    const expiresAt = manualExpiresAt ? new Date(manualExpiresAt).toISOString() : null;
    await runAction("manual_token_update", "Manual Provider token saved", {
      accessToken: manualToken,
      expiresAt,
    });
    setManualToken("");
    setManualExpiresAt("");
    setManualConfirmed(false);
    setManualOpen(false);
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Provider? New App jobs will pause until Provider is connected again.")) return;
    await runAction("disconnect", "Provider disconnected");
  };

  const connection = status?.connection;
  const HealthIcon = statusIcon(connection?.health ?? "disconnected");
  const deviceOAuthReady = status?.runtime.deviceOAuthConfigured && status?.runtime.encryptionConfigured;
  const providerIdentity = connection?.provider_identity ?? null;
  const providerIdentityAccount = providerIdentity?.user_email ?? null;
  const providerIdentityWorkspace = providerIdentity?.workspace_name || providerIdentity?.workspace_id || providerIdentity?.organization_id || null;
  const providerIdentityPlan = providerIdentity?.tier_display_name || providerIdentity?.tier || null;
  const storedAccount = connection?.account_email || connection?.account_id || null;
  const appAccessProbe = connection?.app_access_probe ?? null;
  const appAccessChecking = busyAction === "test_connection";
  const AppAccessIcon = appAccessIcon(appAccessProbe, appAccessChecking);
  const accountMismatch = Boolean(
    connection?.account_email &&
    providerIdentityAccount &&
    connection.account_email.toLowerCase() !== providerIdentityAccount.toLowerCase(),
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-semibold text-foreground">
            <Plug className="h-5 w-5" />
            Provider Connection
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            OAuth token health for App jobs.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void loadStatus()} disabled={loading}>
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Refresh
        </Button>
      </div>

      {!loading && status && !deviceOAuthReady && (
        <Alert variant="destructive">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Provider authorization setup incomplete</AlertTitle>
          <AlertDescription>
            Missing {!status.runtime.encryptionConfigured ? "BASE44_TOKEN_ENCRYPTION_KEY" : "Provider device authorization configuration"}.
          </AlertDescription>
        </Alert>
      )}

      {connection && (
        <Card className="border-border/60">
          <CardContent className="p-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <HealthIcon className="h-5 w-5 text-muted-foreground" />
                  <Badge variant="outline" className={cn("capitalize", healthClass(connection.health))}>
                    {HEALTH_LABELS[connection.health] ?? connection.health}
                  </Badge>
                  <Badge variant="secondary" className="capitalize">
                    {connection.auth_mode.replace(/_/g, " ")}
                  </Badge>
                </div>
                <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2 xl:grid-cols-3">
                  <div>
                    <dt className="text-muted-foreground">Account</dt>
                    <dd className="truncate font-medium text-foreground">{connection.account_email || connection.account_id || "Unknown"}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Workspace</dt>
                    <dd className="truncate font-medium text-foreground">{connection.workspace_name || connection.workspace_id || "Unknown"}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Token expiry</dt>
                    <dd className="font-medium text-foreground">{formatExpiry(connection.token_expires_at)}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Last check</dt>
                    <dd className="font-medium text-foreground">{formatDate(connection.last_checked_at)}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Last refresh</dt>
                    <dd className="font-medium text-foreground">{formatDate(connection.last_refresh_at)}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Last success</dt>
                    <dd className="font-medium text-foreground">{formatDate(connection.last_success_at)}</dd>
                  </div>
                </dl>
                {connection.last_error && (
                  <div className="mt-4 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-900">
                    {providerText(connection.last_error)}
                  </div>
                )}
                {connection.health === "reconnect_required" && (
                  <Alert variant="destructive" className="mt-4">
                    <ShieldAlert className="h-4 w-4" />
                    <AlertTitle>Reconnect required</AlertTitle>
                    <AlertDescription>
                      The saved authorization can no longer refresh. Sign in to the intended Provider account, use Reconnect below, then run Test connection. Automatic checks will not replace the saved credential.
                    </AlertDescription>
                  </Alert>
                )}
                {connection.health === "failed" && (
                  <p className="mt-4 text-sm text-muted-foreground">Refresh failed temporarily. A scheduled check will retry; use Test connection to check current access.</p>
                )}

                <div className="mt-5 border-t border-border/60 pt-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-sm font-semibold text-foreground">Provider identity</h3>
                    {providerIdentityPlan && (
                      <Badge variant="secondary" className="capitalize">
                        {providerIdentityPlan}
                      </Badge>
                    )}
                    {providerIdentity?.is_over_limit && (
                      <Badge variant="outline" className="border-amber-500/50 bg-amber-500/10 text-amber-800">
                        Usage API says over limit
                      </Badge>
                    )}
                  </div>

                  <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-2 xl:grid-cols-3">
                    <div>
                      <dt className="text-muted-foreground">Stored account</dt>
                      <dd className="truncate font-medium text-foreground">{formatIdentityValue(storedAccount)}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Usage account</dt>
                      <dd className="truncate font-medium text-foreground">{formatIdentityValue(providerIdentityAccount)}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Workspace / org</dt>
                      <dd className="truncate font-medium text-foreground">{formatIdentityValue(providerIdentityWorkspace)}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Plan from API</dt>
                      <dd className="font-medium text-foreground">{formatIdentityValue(providerIdentityPlan)}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Message credits</dt>
                      <dd className="font-medium text-foreground">{formatUsage(providerIdentity?.monthly_usage, providerIdentity?.monthly_limit)}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Integration credits</dt>
                      <dd className="font-medium text-foreground">{formatUsage(providerIdentity?.monthly_integrations_usage, providerIdentity?.monthly_integrations_limit)}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Usage renews</dt>
                      <dd className="font-medium text-foreground">{formatDate(providerIdentity?.next_period_start_date ?? providerIdentity?.current_credit_period_end_date)}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Identity checked</dt>
                      <dd className="font-medium text-foreground">{formatDate(connection.provider_identity_checked_at ?? providerIdentity?.observed_at)}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Token scopes</dt>
                      <dd className="truncate font-medium text-foreground">{connection.scopes.length > 0 ? connection.scopes.join(", ") : "Unknown"}</dd>
                    </div>
                  </dl>

                  {accountMismatch && (
                    <div className="mt-4 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-900">
                      Stored account and usage account do not match. Reconnect while signed into the intended Provider workspace, then run Test again.
                    </div>
                  )}

                  {connection.provider_identity_error && (
                    <div className="mt-4 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-900">
                      {providerText(connection.provider_identity_error)}
                    </div>
                  )}
                </div>

                <div className="mt-5 border-t border-border/60 pt-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
                      <Activity className="h-4 w-4 text-muted-foreground" />
                      Provider app access
                    </h3>
                    <Badge variant="outline" className={cn("capitalize", appAccessStatusClass(appAccessProbe, appAccessChecking))}>
                      <AppAccessIcon className={cn("mr-1 h-3.5 w-3.5", appAccessChecking && "animate-spin")} />
                      {appAccessStatusLabel(appAccessProbe, appAccessChecking)}
                    </Badge>
                  </div>

                  {appAccessProbe ? (
                    <>
                      <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-2 xl:grid-cols-4">
                        <div>
                          <dt className="text-muted-foreground">Last app check</dt>
                          <dd className="font-medium text-foreground">{formatDate(appAccessProbe.checkedAt)}</dd>
                        </div>
                        <div>
                          <dt className="text-muted-foreground">Target</dt>
                          <dd className="font-medium text-foreground">{targetSourceLabel(appAccessProbe.targetSource)}</dd>
                        </div>
                        <div>
                          <dt className="text-muted-foreground">Provider app state</dt>
                          <dd className="font-medium text-foreground">{formatIdentityValue(appAccessProbe.appStatus?.state ?? appAccessProbe.appStatus?.providerStatus)}</dd>
                        </div>
                        <div>
                          <dt className="text-muted-foreground">Preview render</dt>
                          <dd className="font-medium text-foreground">
                            {previewLabel(appAccessProbe.preview?.classification)}
                            {typeof appAccessProbe.preview?.httpStatus === "number" ? ` (HTTP ${appAccessProbe.preview.httpStatus})` : ""}
                          </dd>
                        </div>
                      </dl>

                      <div className="mt-3 grid gap-2 text-xs text-muted-foreground sm:grid-cols-2 xl:grid-cols-4">
                        <div className="flex items-center gap-2 rounded-md bg-muted/40 px-3 py-2">
                          <Server className="h-4 w-4 shrink-0" />
                          <span>{typeof appAccessProbe.toolCount === "number" ? `${appAccessProbe.toolCount} Provider capabilities visible` : "Provider capabilities unknown"}</span>
                        </div>
                        <div className="flex items-center gap-2 rounded-md bg-muted/40 px-3 py-2">
                          <CheckCircle2 className="h-4 w-4 shrink-0" />
                          <span>Status check: {yesNo(appAccessProbe.tools?.getAppStatus)}</span>
                        </div>
                        <div className="flex items-center gap-2 rounded-md bg-muted/40 px-3 py-2">
                          <Eye className="h-4 w-4 shrink-0" />
                          <span>Preview check: {yesNo(appAccessProbe.preview?.toolSucceeded)}</span>
                        </div>
                        <div className="flex items-center gap-2 rounded-md bg-muted/40 px-3 py-2">
                          <CheckCircle2 className="h-4 w-4 shrink-0" />
                          <span>Page title detected: {yesNo(appAccessProbe.preview?.hasTitle)}</span>
                        </div>
                      </div>

                      {appAccessProbe.reason && (
                        <div className="mt-3 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-900">
                          {providerText(appAccessProbe.reason)}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="mt-3 rounded-md border border-dashed border-border px-3 py-3 text-sm text-muted-foreground">
                      Run Test to verify that the connected Provider account can see and open a stored App.
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap gap-2 lg:max-w-sm lg:justify-end">
                <Button onClick={() => void startDeviceOAuth()} disabled={!deviceOAuthReady || !!busyAction}>
                  {busyAction === "device_oauth_start" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plug className="mr-2 h-4 w-4" />}
                  {connection.health === "disconnected" ? "Connect" : "Reconnect"}
                </Button>
                <Button variant="outline" onClick={() => void runAction("refresh_now", "Provider token refreshed")} disabled={!!busyAction || connection.health === "disconnected"}>
                  {busyAction === "refresh_now" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
                  Refresh now
                </Button>
                <Button variant="outline" onClick={() => void runAction("test_connection", "Provider connection verified")} disabled={!!busyAction || connection.health === "disconnected"}>
                  {busyAction === "test_connection" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
                  Test
                </Button>
                <Button variant="outline" onClick={() => void runAction("resume_blocked_jobs", "Blocked App jobs resumed")} disabled={!!busyAction || status.blocked.jobs === 0}>
                  Resume paused jobs
                </Button>
                <Button variant="outline" onClick={() => setManualOpen(true)} disabled={!!busyAction}>
                  <KeyRound className="mr-2 h-4 w-4" />
                  Manual override
                </Button>
                <Button variant="ghost" onClick={() => void disconnect()} disabled={!!busyAction || connection.health === "disconnected"}>
                  <Unplug className="mr-2 h-4 w-4" />
                  Disconnect
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {status && (
        <div className="grid gap-4 md:grid-cols-3">
          <Card className="border-border/60">
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground">Paused App jobs</div>
              <div className="mt-2 text-2xl font-semibold text-foreground">{status.blocked.jobs}</div>
            </CardContent>
          </Card>
          <Card className="border-border/60">
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground">Blocked new apps</div>
              <div className="mt-2 text-2xl font-semibold text-foreground">{status.blocked.auth_blocked_apps}</div>
            </CardContent>
          </Card>
          <Card className="border-border/60">
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground">Paused updates</div>
              <div className="mt-2 text-2xl font-semibold text-foreground">{status.blocked.update_auth_blocked_apps}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {status && blockedTotal > 0 && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Jobs are paused for Provider auth</AlertTitle>
          <AlertDescription>
            Reconnect or refresh Provider, test the connection, then resume paused jobs. Attempts have not been incremented while auth is blocked.
          </AlertDescription>
        </Alert>
      )}

      {loading && !status && (
        <div className="flex h-48 items-center justify-center text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading Provider connection...
        </div>
      )}

      <Dialog open={deviceOpen} onOpenChange={setDeviceOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Connect Provider</DialogTitle>
            <DialogDescription>
              Sign in to Provider and confirm this one-time code. This page will detect completion automatically.
            </DialogDescription>
          </DialogHeader>
          {deviceAuth && (
            <div className="space-y-4">
              <div className="rounded-md border border-border bg-muted/40 p-4 text-center">
                <div className="text-sm text-muted-foreground">Confirmation code</div>
                <div className="mt-2 font-mono text-2xl font-semibold tracking-widest text-foreground">{deviceAuth.user_code}</div>
              </div>
              <Button asChild className="w-full">
                <a href={deviceAuth.verification_uri} target="_blank" rel="noreferrer">
                  Open Provider sign-in
                </a>
              </Button>
              <div className="flex items-center justify-center text-sm text-muted-foreground">
                {devicePolling && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Waiting for authorization...
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeviceOpen(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Manual token override</DialogTitle>
            <DialogDescription>
              Use this only when OAuth is unavailable. The token is encrypted server-side and never returned to the browser.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="base44-token">Access token</Label>
              <Textarea
                id="base44-token"
                value={manualToken}
                onChange={(event) => setManualToken(event.target.value)}
                placeholder="Paste Provider MCP OAuth token"
                className="min-h-28 font-mono text-xs"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="base44-expires">Expiry</Label>
              <Input
                id="base44-expires"
                type="datetime-local"
                value={manualExpiresAt}
                onChange={(event) => setManualExpiresAt(event.target.value)}
              />
            </div>
            <label className="flex items-start gap-2 rounded-md border border-border p-3 text-sm">
              <Checkbox checked={manualConfirmed} onCheckedChange={(checked) => setManualConfirmed(checked === true)} />
              <span>
                I understand this is a degraded break-glass mode and should be replaced with OAuth refresh credentials.
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualOpen(false)}>Cancel</Button>
            <Button onClick={() => void saveManualToken()} disabled={!manualToken.trim() || !manualConfirmed || !!busyAction}>
              {busyAction === "manual_token_update" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save token
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
